import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { BASE_SITE_CONTEXT_ID, BaseSiteService as CxBaseSiteService, SiteContextConfig, StateUtils } from '@spartacus/core';
import { Observable } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { BaseSite, SiteConfig } from '../../model/misc.model';
import { WindowRef } from '../../window/window-ref';
import { getContextParameterValues } from '../config/context-config-utils';
import { SiteContextActions } from '../store/actions/index';
import { SiteContextSelectors } from '../store/selectors/index';
import { StateWithSiteContext } from '../store/site-context-state';
import { SiteContext } from './site-context.interface';

@Injectable()
export class BaseSiteService extends CxBaseSiteService implements SiteContext<BaseSite> {
  constructor(protected store: Store<StateWithSiteContext>, protected config: SiteContextConfig, protected winRef: WindowRef) {
    super(store, config);
  }

  /**
   * Represents the current baseSite uid.
   */
  getActive(): Observable<string> {
    return this.store.pipe(
      select(SiteContextSelectors.getActiveBaseSite),
      filter((active) => Boolean(active))
    );
  }

  /**
   * Get all base sites data
   */
  getAll(): Observable<BaseSite[]> {
    return this.store.pipe(
      select(SiteContextSelectors.getAllBaseSites),
      tap((sites) => {
        if (!sites) {
          this.store.dispatch(new SiteContextActions.LoadBaseSites());
        }
      }),
      filter((sites) => Boolean(sites))
    );
  }

  /**
   * Get base site data based on site uid
   */
  get(siteUid?: string): Observable<BaseSite | undefined> {
    if (siteUid) {
      return this.getAll().pipe(map((sites) => sites.find((site) => site.uid === siteUid)));
    }

    return this.getActive().pipe(
      switchMap((activeSiteUid) => this.getAll().pipe(map((sites) => sites.find((site) => site.uid === activeSiteUid))))
    );
  }

  setActive(baseSite: string): void {
    this.store.pipe(select(SiteContextSelectors.getActiveBaseSite), take(1)).subscribe((activeBaseSite) => {
      if (baseSite && activeBaseSite !== baseSite) {
        this.store.dispatch(new SiteContextActions.SetActiveBaseSite(baseSite));
      }
    });
  }

  /**
   * Check if current base site is a fallback base site
   */
  isFallback(): boolean {
    let isFallback: boolean;
    this.store
      .pipe(select(SiteContextSelectors.getIsFallbackBaseSite))
      .subscribe((value) => (isFallback = value))
      .unsubscribe();

    return isFallback;
  }

  setIsFallback(isFallback: boolean): void {
    this.store.dispatch(new SiteContextActions.SetIsFallbackBaseSite(isFallback));
  }

  /**
   * Tells whether the value of the base site has been already initialized
   */
  isInitialized(): boolean {
    let valueInitialized = false;
    this.getActive()
      .subscribe(() => (valueInitialized = true))
      .unsubscribe();

    return valueInitialized;
  }

  getConfig(): SiteContextConfig {
    return this.config;
  }

  /**
   * Returns current site configuration (dispatch action to load configuration if not loaded yet)
   */
  getSiteConfig(): Observable<SiteConfig> {
    return this.store.select(SiteContextSelectors.getSiteConfig).pipe(
      filter((loaderState) => {
        if (this.needsToLoad(loaderState)) {
          this.loadSiteConfig();
          return false;
        }
        return loaderState.success;
      }),
      map((loaderState) => loaderState.value)
    );
  }

  /**
   * Tells whether the given base site is allowed.
   *
   * The list of allowed base sites can be configured in the `context` config of Spartacus.
   */
  protected isValid(value: string): boolean {
    return !!value && getContextParameterValues(this.config, BASE_SITE_CONTEXT_ID).includes(value);
  }

  private loadSiteConfig(): void {
    this.store.dispatch(new SiteContextActions.LoadSiteConfig());
  }

  private needsToLoad(loaderState: StateUtils.LoaderState<SiteConfig>) {
    return !loaderState.success && !loaderState.error && !loaderState.loading;
  }
}
